import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../utils/api';
import { supabase } from '../utils/supabase';

const AuthContext = createContext();

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLoggingOut = useRef(false);

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  useEffect(() => {
    // ── On app load: check token ──
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      if (parsed?.token && isTokenExpired(parsed.token)) {
        logout();
      } else {
        setUser(parsed);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
  const reqInterceptor = api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Replace only the resInterceptor block
  const resInterceptor = api.interceptors.response.use(
    (response) => response,
    (error) => {
      // ── Case 1: Token expired or missing ──
      if (error.response?.status === 401) {
        const token = localStorage.getItem('token');
        const tokenIsExpiredOrMissing = !token || isTokenExpired(token);
        const isOnLoginPage = window.location.pathname === '/login';

        if (tokenIsExpiredOrMissing && !isLoggingOut.current && !isOnLoginPage) {
          isLoggingOut.current = true;
          logout();
          window.location.href = '/login';
        }
      }

      // ── Case 2: User banned or deleted ──
      if (error.response?.status === 403) {
        const reason = error.response?.data?.reason; // 'banned' or 'deleted'
        if ((reason === 'banned' || reason === 'deleted') && !isLoggingOut.current) {
          isLoggingOut.current = true;
          logout();
          window.location.href = `/login?reason=${reason}`;
        }
      }

      return Promise.reject(error);
    }
  );

    // Cleanup on unmount
    return () => {
      api.interceptors.response.eject(resInterceptor);
      api.interceptors.request.eject(reqInterceptor);
    };
  }, []);

  const login = async (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userData.token);
    setUser(userData);

    // ✅ Save FCM token with JWT auth header
    const fcmToken = localStorage.getItem('fcm_token');
    if (fcmToken) {
      try {
        await fetch('https://elite-goats.onrender.com/api/auth/save-fcm-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userData.token}`
          },
          body: JSON.stringify({ fcmToken, userId: userData.id })
        });
        console.log('✅ FCM token saved for user:', userData.id);
      } catch (err) {
        console.error('FCM save failed:', err.message);
      }
    }
  };

  const updateUser = (updatedFields) => {
  setUser(prev => {
    const updated = { ...prev, ...updatedFields };
    localStorage.setItem('user', JSON.stringify(updated));
    return updated;
  });
};

  // ── Supabase database query helper ──
  const supabaseQuery = async (table, operation = 'select', options = {}) => {
    try {
      let query = supabase.from(table);
      
      switch(operation) {
        case 'select':
          return await query.select(options.select || '*').match(options.match || {});
        case 'insert':
          return await query.insert([options.data]);
        case 'update':
          return await query.update(options.data).eq('id', options.id);
        case 'delete':
          return await query.delete().eq('id', options.id);
        default:
          return await query.select();
      }
    } catch (error) {
      console.error('Supabase error:', error);
      return { data: null, error };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, supabaseQuery, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);